import React, { useEffect, useState, useRef, useMemo } from "react";
import axios from "axios";
import {
  Send, MessageCircle, Clock, AlertTriangle, CheckCircle, ExternalLink,
  UserX, ArrowUpRight, Megaphone, X, Search, CheckSquare, Square, Zap, Users
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const MERGE_FIELDS = ["{first_name}", "{last_name}", "{agent_name}", "{address}"];
const SMS_MAX = 320;
const URL_PATTERN = /https?:\/\/|www\.|\.com\/|\.net\/|\.org\//i;

function StatusDot({ status }) {
  if (status === "delivered") return <span className="inline-block w-2 h-2 rounded-full bg-green-500 ml-1" title="Delivered" />;
  if (status === "failed") return <span className="inline-block w-2 h-2 rounded-full bg-red-500 ml-1" title="Failed" />;
  return <span className="inline-block w-2 h-2 rounded-full bg-slate-400 ml-1" title={status} />;
}

export default function FubWidget() {
  const [ctx, setCtx] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [widgetToken, setWidgetToken] = useState(null);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  const [messages, setMessages] = useState([]);
  const [showBlastModal, setShowBlastModal] = useState(false);
  const messagesEndRef = useRef(null);

  // Load FUB context
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const context = params.get("context");
    const signature = params.get("signature") || "";
    if (!context) {
      setError("No context provided. This page is meant to be loaded by FollowUp Boss.");
      setLoading(false);
      return;
    }
    axios.get(`${API}/api/fub/widget/context`, { params: { context, signature } })
      .then(r => {
        setCtx(r.data);
        setMessages(r.data.messages || []);
        if (r.data.widget_token) setWidgetToken(r.data.widget_token);
      })
      .catch(e => setError(e.response?.data?.detail || "Failed to load context"))
      .finally(() => setLoading(false));
  }, []);

  // Auto-scroll to latest message
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Load FUB Embedded App SDK
  useEffect(() => {
    const script = document.createElement("script");
    script.src = "https://eia.followupboss.com/embeddedApps-v1.0.1.js";
    script.type = "text/javascript";
    script.onload = () => {
      if (window.EmbeddedApp) {
        window.EmbeddedApp.init();
      }
    };
    document.head.appendChild(script);
    return () => { try { document.head.removeChild(script); } catch (_) {} };
  }, []);

  const sendMessage = async () => {
    if (!reply.trim() || !ctx?.contact?.id) return;
    setSending(true);
    try {
      const r = await axios.post(`${API}/api/fub/widget/send`, {
        contact_id: ctx.contact.id,
        body: reply,
      }, {
        headers: widgetToken ? { Authorization: `Bearer ${widgetToken}` } : {},
      });
      setMessages(prev => [...prev, r.data]);
      setReply("");
    } catch (e) {
      alert(e.response?.data?.detail || "Send failed");
    }
    setSending(false);
  };

  const dashboardUrl = window.location.origin;

  // ─── Loading State ──────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-white">
        <div className="flex flex-col items-center gap-3">
          <div className="w-7 h-7 border-2 border-[#FF5C35] border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-slate-500">Loading Relay...</p>
        </div>
      </div>
    );
  }

  // ─── Error State ────────────────────────────────────────────────────────────
  if (error) {
    return (
      <div className="flex items-center justify-center h-screen bg-white p-6">
        <div className="text-center max-w-sm">
          <AlertTriangle size={32} className="text-amber-500 mx-auto mb-3" />
          <p className="text-base font-semibold text-slate-800 mb-2">Unable to load</p>
          <p className="text-sm text-slate-500 mb-4">{error}</p>
          <a href={dashboardUrl} target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-sm text-[#FF5C35] hover:text-[#E84E29] font-medium">
            Open Relay <ExternalLink size={14} />
          </a>
        </div>
      </div>
    );
  }

  const person = ctx?.person;
  const contact = ctx?.contact;
  const isOptedOut = ctx?.opted_out;
  const hasContact = Boolean(contact);
  const authStatus = ctx?.auth_status;
  const fubUserEmail = ctx?.fub_user_email;

  // ─── No Relay Account Found ─────────────────────────────────────────
  if (authStatus === "no_account") {
    return (
      <div className="flex flex-col items-center justify-center h-screen bg-white p-6 text-center">
        <div className="w-12 h-12 rounded-full bg-amber-50 flex items-center justify-center mb-4">
          <AlertTriangle size={24} className="text-amber-500" />
        </div>
        <p className="text-base font-semibold text-slate-800 mb-2">Account Required</p>
        <p className="text-sm text-slate-500 mb-1">
          No Relay account found for:
        </p>
        <p className="text-sm font-medium text-slate-700 mb-4">
          {fubUserEmail || "unknown email"}
        </p>
        <p className="text-xs text-slate-400 mb-4">
          Sign up with the same email you use in FollowUp Boss to access SMS features.
        </p>
        <a href={`${dashboardUrl}/register`} target="_blank" rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 text-sm bg-[#FF5C35] text-white px-4 py-2.5 rounded-lg hover:bg-[#E84E29] font-semibold">
          Create Account <ExternalLink size={14} />
        </a>
        <a href={dashboardUrl} target="_blank" rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 text-sm text-[#FF5C35] hover:text-[#E84E29] font-medium mt-3">
          Already have an account? Log in <ExternalLink size={14} />
        </a>
      </div>
    );
  }

  // ─── Contact Not Synced ─────────────────────────────────────────────────────
  if (ctx?.debug_state === "account_not_found" || (!hasContact && !ctx?.is_example)) {
    return (
      <div className="flex flex-col items-center justify-center h-screen bg-white p-6 text-center">
        <div className="w-12 h-12 rounded-full bg-amber-50 flex items-center justify-center mb-4">
          <AlertTriangle size={24} className="text-amber-500" />
        </div>
        <p className="text-base font-semibold text-slate-800 mb-2">Contact not synced</p>
        <p className="text-sm text-slate-500 mb-4">
          {person?.firstName} {person?.lastName} hasn't been synced to Relay yet.
        </p>
        <a href={`${dashboardUrl}/settings`} target="_blank" rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 text-sm bg-[#FF5C35] text-white px-4 py-2 rounded-lg hover:bg-[#E84E29] font-medium">
          Sync in Settings <ExternalLink size={14} />
        </a>
      </div>
    );
  }

  // ─── Main Widget ────────────────────────────────────────────────────────────
  return (
    <div className="flex flex-col h-screen bg-white" style={{ fontFamily: "Manrope, sans-serif" }}>

      {/* ── Branded Header ── */}
      <div className="px-4 py-2.5 shrink-0 flex items-center gap-2.5" style={{ backgroundColor: "#172937", borderBottom: "2px solid #FF5C35" }}>
        <img src="/widget-logo.svg" alt="" className="w-5 h-5" />
        <span className="text-sm font-bold tracking-wide" style={{ color: "#ffffff" }}>relay</span>
      </div>

      {/* ── Contact Info + Action Buttons ── */}
      <div className="px-4 py-3 border-b border-slate-100 bg-white shrink-0">
        <div className="flex items-center gap-3">
          {/* Avatar */}
          <div className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold shrink-0" style={{ backgroundColor: "#FF5C35", color: "#ffffff" }}>
            {(person?.firstName?.[0] || "").toUpperCase()}
          </div>

          {/* Name + Phone */}
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-slate-900 text-sm truncate leading-tight">
              {person?.firstName} {person?.lastName}
            </p>
            <p className="text-slate-400 text-xs truncate leading-tight">{ctx?.phone || "No phone"}</p>
          </div>

          {/* Status Badge */}
          <div className="shrink-0">
            {isOptedOut ? (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-slate-100 text-slate-500 rounded-full border border-slate-200 text-xs font-medium">
                <UserX size={10} /> Opted Out
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-green-50 text-green-700 rounded-full border border-green-200 text-xs font-medium">
                <CheckCircle size={11} className="text-green-600" /> Active
              </span>
            )}
          </div>
        </div>

        {/* Action Buttons Row */}
        <div className="flex gap-2 mt-3">
          <button
            onClick={() => setShowBlastModal(true)}
            className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold transition-colors"
            style={{ backgroundColor: "#FF5C35", color: "#ffffff" }}
            data-testid="widget-open-blast"
          >
            <Megaphone size={14} />
            Mass Blast
          </button>
          <a
            href={dashboardUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-colors border border-slate-300 text-slate-700 hover:bg-slate-50"
            data-testid="widget-open-dashboard"
          >
            Go to Dashboard <ArrowUpRight size={12} />
          </a>
        </div>
      </div>

      {/* ── Message Thread ── */}
      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-2" style={{ backgroundColor: "#f1f5f9", minHeight: "180px" }}>
        {messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center py-6">
            <p className="text-sm text-slate-400">Text History here</p>
            {!isOptedOut && hasContact && (
              <p className="text-xs text-slate-300 mt-1">Send the first message below</p>
            )}
          </div>
        ) : messages.map(m => (
          <div key={m.id} className={`flex ${m.direction === "outbound" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[85%] px-3 py-2 rounded-2xl text-sm leading-relaxed ${
              m.direction === "outbound"
                ? "bg-[#FF5C35] text-white rounded-br-sm"
                : "bg-white text-slate-800 border border-slate-200 rounded-bl-sm"
            }`}>
              <p>{m.body}</p>
              <div className={`flex items-center justify-end gap-1 mt-1 ${m.direction === "outbound" ? "text-[#D1D5DB]" : "text-slate-400"}`} style={{ fontSize: "10px" }}>
                <Clock size={9} />
                {new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                {m.direction === "outbound" && <StatusDot status={m.status} />}
              </div>
            </div>
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>

      {/* ── Reply Composer ── */}
      <div className="px-4 py-3 border-t border-slate-200 bg-white shrink-0">
        {isOptedOut ? (
          <div className="text-center py-2 text-xs text-slate-500 flex items-center justify-center gap-1.5">
            <UserX size={14} className="text-slate-400" /> Contact has opted out of SMS
          </div>
        ) : !hasContact ? (
          <div className="text-center py-2 text-xs text-slate-500">Sync contacts to enable messaging</div>
        ) : (
          <div className="space-y-2">
            <textarea
              value={reply}
              onChange={e => setReply(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
              rows={2}
              placeholder="Type a message here (Enter to send)"
              className="w-full text-sm px-3 py-2.5 border border-slate-300 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-[#FF5C35] focus:border-transparent"
              data-testid="widget-reply-input"
            />
            {/* SMS Guardrails */}
            {reply.length > 0 && (
              <div className="space-y-1">
                <div className="flex items-center justify-between">
                  <span className={`text-[10px] font-medium ${reply.length > SMS_MAX ? "text-red-600" : reply.length > 250 ? "text-amber-600" : "text-slate-400"}`}>
                    {reply.length}/{SMS_MAX} · {reply.length <= 160 ? 1 : Math.ceil(reply.length / 153)} seg
                  </span>
                </div>
                <div className="h-0.5 bg-slate-200 rounded-full overflow-hidden">
                  <div className={`h-full rounded-full transition-all ${reply.length > SMS_MAX ? "bg-red-500" : reply.length > 250 ? "bg-amber-500" : "bg-[#FF5C35]"}`} style={{ width: `${Math.min((reply.length / SMS_MAX) * 100, 100)}%` }} />
                </div>
                {reply.length > SMS_MAX && <p className="text-[10px] text-red-600">Too long — carriers may block delivery.</p>}
                {URL_PATTERN.test(reply) && <p className="text-[10px] text-red-600">URLs may trigger carrier spam filters.</p>}
              </div>
            )}
            <button
              onClick={sendMessage}
              disabled={sending || !reply.trim() || reply.length > SMS_MAX}
              className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-semibold transition-colors disabled:opacity-50"
              style={{ backgroundColor: "#FF5C35", color: "#ffffff" }}
              data-testid="widget-send-btn"
            >
              {sending ? (
                <span className="flex items-center gap-2"><span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin inline-block" /> Sending...</span>
              ) : (
                <span className="flex items-center gap-2"><Send size={15} /> Send Message</span>
              )}
            </button>
          </div>
        )}
      </div>

      {/* ── Mass Blast Modal ── */}
      {showBlastModal && (
        <BlastModal
          widgetToken={widgetToken}
          onClose={() => setShowBlastModal(false)}
          connectedAgent={ctx?.fub_user?.name || ctx?.fub_user_email || null}
          agentName={ctx?.fub_user?.name || "Your Agent"}
        />
      )}
    </div>
  );
}


// ─── Mass Blast Modal (full-screen overlay within widget) ────────────────────
function BlastModal({ widgetToken, onClose, connectedAgent, agentName }) {
  const [audienceMode, setAudienceMode] = useState("synced");
  const [smartLists, setSmartLists] = useState([]);
  const [selectedList, setSelectedList] = useState("");
  const [contacts, setContacts] = useState([]);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [totalInFub, setTotalInFub] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [filterStage, setFilterStage] = useState("");
  const [stages, setStages] = useState([]);
  const [search, setSearch] = useState("");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [filterTag, setFilterTag] = useState("");
  const [allTags, setAllTags] = useState([]);
  const [totalStats, setTotalStats] = useState({ total: 0, stages: [], tags: [] });
  const [useFilter, setUseFilter] = useState(true); // true = send by filter, false = pick individual contacts

  const api = useMemo(() =>
    axios.create({ baseURL: API, headers: widgetToken ? { Authorization: `Bearer ${widgetToken}` } : {} }),
    [widgetToken]
  );

  const [ponds, setPonds] = useState([]);

  // Load smart lists + ponds + stages + total count on mount
  useEffect(() => {
    if (!widgetToken) return;
    api.get("/api/fub/smart-lists").then(r => setSmartLists(r.data.smart_lists || [])).catch(() => {});
    api.get("/api/fub/ponds").then(r => setPonds(r.data.ponds || [])).catch(() => {});
    api.get("/api/contacts/stats").then(r => {
      const stageList = (r.data.stages || []);
      const tagList = (r.data.top_tags || []);
      setStages(stageList.map(s => s.stage));
      setAllTags(tagList);
      setTotalStats({ total: r.data.total || 0, stages: stageList, tags: tagList });
    }).catch(() => {});
  }, [widgetToken, api]);

  // Load synced contacts (only when picking individual contacts)
  useEffect(() => {
    if (audienceMode !== "synced" || !widgetToken || useFilter) return;
    setLoadingContacts(true);
    const params = { limit: 0, ...(filterStage && { stage: filterStage }), ...(filterTag && { tag: filterTag }), ...(search && { search }) };
    api.get("/api/contacts", { params })
      .then(r => {
        const active = (r.data.contacts || []).filter(c => !c.opted_out);
        setContacts(active);
      })
      .catch(() => {})
      .finally(() => setLoadingContacts(false));
  }, [audienceMode, filterStage, filterTag, search, widgetToken, api, useFilter]);

  const loadListContacts = async () => {
    if (!selectedList) return;
    setLoadingContacts(true);
    try {
      let url;
      if (selectedList.startsWith("pond:")) {
        url = `/api/fub/ponds/${selectedList.replace("pond:", "")}/contacts`;
      } else {
        url = `/api/fub/smart-lists/${selectedList.replace("smartlist:", "")}/contacts`;
      }
      const r = await api.get(url);
      const loaded = r.data.contacts || [];
      setContacts(loaded);
      setSelectedIds(loaded.map(c => c.id));
      setTotalInFub(r.data.total_in_fub || null);
    } catch (e) {
      alert("Failed to load contacts");
    }
    setLoadingContacts(false);
  };

  const toggleContact = id => setSelectedIds(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);
  const selectAll = () => setSelectedIds(contacts.map(c => c.id));
  const clearAll = () => setSelectedIds([]);

  const getFilterCount = () => {
    if (filterTag) {
      const match = (totalStats.tags || []).find(t => t.tag === filterTag);
      return match ? match.count : 0;
    }
    if (filterStage) {
      const match = totalStats.stages.find(s => s.stage === filterStage);
      return match ? match.count : 0;
    }
    return totalStats.total;
  };

  const [blastProgress, setBlastProgress] = useState(null);

  const sendBlast = async () => {
    if (!body.trim()) return;
    setSending(true);
    setResult(null);
    setBlastProgress(null);
    try {
      let payload = { body };
      if (audienceMode === "synced" && useFilter) {
        if (filterStage) payload.blast_stage = filterStage;
        if (filterTag) payload.blast_tag = filterTag;
        if (!filterStage && !filterTag) payload.blast_all = true;
      } else {
        if (selectedIds.length === 0) { setSending(false); return; }
        payload.contact_ids = selectedIds;
      }
      const r = await api.post("/api/blasts", payload);

      if (r.data.blast_id) {
        setBlastProgress({ processed: 0, sendable: r.data.sendable, sent: 0, failed: 0 });
        const poll = setInterval(async () => {
          try {
            const s = await api.get(`/api/blasts/${r.data.blast_id}/status`);
            const d = s.data;
            setBlastProgress({ processed: d.processed, sendable: d.sendable, sent: d.sent, failed: d.failed });
            if (d.status === "sent" || d.status === "failed") {
              clearInterval(poll);
              setResult({ sent: d.sent, failed: d.failed, opted_out: d.opted_out, total: d.total, mock: d.mock });
              setBlastProgress(null);
              setSending(false);
              setSelectedIds([]);
            }
          } catch (_) {}
        }, 2000);
      } else {
        setResult(r.data);
        setSelectedIds([]);
        setSending(false);
      }
    } catch (e) {
      alert(e.response?.data?.detail || "Blast failed");
      setSending(false);
    }
  };

  // ─── No Auth ────────────────────────────────────────────────────────────────
  if (!widgetToken) {
    return (
      <div className="fixed inset-0 bg-white z-50 flex flex-col">
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 shrink-0">
          <h2 className="text-base font-semibold text-slate-800 flex items-center gap-2">
            <Megaphone size={18} className="text-[#FF5C35]" /> Mass Blast
          </h2>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-slate-100">
            <X size={18} className="text-slate-500" />
          </button>
        </div>
        <div className="flex-1 flex flex-col items-center justify-center p-6 text-center">
          <AlertTriangle size={28} className="text-amber-500 mb-3" />
          <p className="text-sm font-semibold text-slate-700 mb-2">Authentication required</p>
          <p className="text-sm text-slate-500 mb-4">Log in to Relay first to enable Mass Blast</p>
          <a href={window.location.origin} target="_blank" rel="noopener noreferrer"
            className="text-sm bg-[#FF5C35] text-white px-4 py-2 rounded-lg hover:bg-[#E84E29] inline-flex items-center gap-1.5 font-medium">
            Open Relay <ExternalLink size={14} />
          </a>
        </div>
      </div>
    );
  }

  // ─── Blast Modal Content ────────────────────────────────────────────────────
  return (
    <div className="fixed inset-0 bg-white z-50 flex flex-col" style={{ fontFamily: "Manrope, sans-serif" }}>

      {/* Modal Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 shrink-0 bg-white">
        <h2 className="text-base font-semibold text-slate-800 flex items-center gap-2">
          <Megaphone size={18} className="text-[#FF5C35]" /> Mass Blast
        </h2>
        <button
          onClick={onClose}
          className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-slate-100 transition-colors"
          data-testid="widget-blast-close"
        >
          <X size={18} className="text-slate-500" />
        </button>
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto bg-slate-50 p-4 space-y-4">

        {/* Connected agent indicator */}
        <div className="flex items-center gap-2 text-xs text-slate-500">
          <CheckCircle size={12} className="text-green-500" />
          <span>Sending as <strong className="text-slate-700">{connectedAgent || "your account"}</strong> — contacts filtered to your assignments</span>
        </div>

        {/* Result banner */}
        {result && (
          <div className="bg-green-50 border border-green-200 rounded-xl p-4" data-testid="widget-blast-result">
            <div className="flex items-center justify-between mb-1">
              <p className="text-sm font-semibold text-green-800">
                {result.mock ? "Simulated" : "Sent!"} — {result.sent} message{result.sent !== 1 ? "s" : ""}
              </p>
              <button onClick={() => setResult(null)} className="text-green-600 hover:text-green-800"><X size={14} /></button>
            </div>
            <p className="text-sm text-green-600">Failed: {result.failed} · Opted out skipped: {result.opted_out}</p>
            {result.mock && <p className="text-xs text-green-500 mt-1">Add SignalWire credentials in Settings to send real SMS</p>}
          </div>
        )}

        {/* Audience Section */}
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <p className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
            <Users size={15} /> Select Audience
          </p>

          {/* Mode toggle */}
          <div className="flex gap-2 mb-3">
            <button
              onClick={() => { setAudienceMode("synced"); setContacts([]); setSelectedIds([]); }}
              className={`flex-1 py-2 text-sm rounded-lg font-medium border transition-colors ${audienceMode === "synced" ? "bg-[#FF5C35] text-white border-[#FF5C35]" : "bg-white text-slate-600 border-slate-300 hover:border-slate-400"}`}
              data-testid="widget-audience-synced"
            >
              Synced Contacts
            </button>
            <button
              onClick={() => { setAudienceMode("smartlist"); setContacts([]); setSelectedIds([]); }}
              className={`flex-1 py-2 text-sm rounded-lg font-medium border transition-colors ${audienceMode === "smartlist" ? "bg-[#FF5C35] text-white border-[#FF5C35]" : "bg-white text-slate-600 border-slate-300 hover:border-slate-400"}`}
              data-testid="widget-audience-smartlist"
            >
              FUB Smart List
            </button>
          </div>

          {audienceMode === "smartlist" ? (
            <div className="space-y-2.5">
              <select
                value={selectedList}
                onChange={e => setSelectedList(e.target.value)}
                className="w-full text-sm px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF5C35] bg-white"
                data-testid="widget-smartlist-select"
              >
                <option value="">Select a Smart List or Pond...</option>
                {smartLists.length > 0 && (
                  <optgroup label="Smart Lists">
                    {smartLists.map(l => <option key={`sl-${l.id}`} value={`smartlist:${l.id}`}>{l.name}</option>)}
                  </optgroup>
                )}
                {ponds.length > 0 && (
                  <optgroup label="Ponds">
                    {ponds.map(p => <option key={`pond-${p.id}`} value={`pond:${p.id}`}>{p.name}</option>)}
                  </optgroup>
                )}
              </select>
              {smartLists.length === 0 && ponds.length === 0 && (
                <p className="text-sm text-slate-400 text-center py-1">No Smart Lists or Ponds found in your FUB account</p>
              )}
              <button
                onClick={loadListContacts}
                disabled={!selectedList || loadingContacts}
                className="w-full py-2 text-sm bg-slate-800 text-white rounded-lg hover:bg-slate-700 disabled:opacity-50 transition-colors font-medium"
                data-testid="widget-load-smartlist-btn"
              >
                {loadingContacts ? "Loading..." : contacts.length > 0 ? `${contacts.length.toLocaleString()}${totalInFub && totalInFub > contacts.length ? ` of ${totalInFub.toLocaleString()}` : ""} loaded — Reload` : "Load Contacts"}
              </button>
            </div>
          ) : (
            <div className="space-y-2.5">
              {/* Stage filter — works for both filter mode and individual mode */}
              <select
                value={filterStage}
                onChange={e => setFilterStage(e.target.value)}
                className="w-full text-sm px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF5C35] bg-white"
                data-testid="widget-blast-stage"
              >
                <option value="">All Contacts ({totalStats.total.toLocaleString()})</option>
                {totalStats.stages.map(s => <option key={s.stage} value={s.stage}>{s.stage} ({s.count.toLocaleString()})</option>)}
              </select>
              <select
                value={filterTag}
                onChange={e => setFilterTag(e.target.value)}
                className="w-full text-sm px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF5C35] bg-white"
                data-testid="widget-blast-tag"
              >
                <option value="">All Tags</option>
                {allTags.map(t => <option key={t.tag} value={t.tag}>{t.tag} ({t.count.toLocaleString()})</option>)}
              </select>

              {/* Filter mode summary */}
              {useFilter ? (
                <div className="bg-[#FFF0EB] border border-[#D1D5DB] rounded-lg p-3 text-center">
                  <p className="text-sm font-semibold" style={{ color: "#0F1419" }}>
                    {filterTag && filterStage ? `${filterStage} + ${filterTag}: ` : filterTag ? `${filterTag}: ` : filterStage ? `${filterStage}: ` : "All contacts: "}{getFilterCount().toLocaleString()} recipients
                  </p>
                  <p className="text-xs text-slate-500 mt-1">Opted-out contacts will be skipped automatically</p>
                  <button
                    onClick={() => setUseFilter(false)}
                    className="text-xs mt-2 underline text-slate-500 hover:text-slate-700"
                  >
                    Or pick individual contacts
                  </button>
                </div>
              ) : (
                <>
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                      <input
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        placeholder="Search name..."
                        className="w-full text-sm pl-9 pr-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF5C35]"
                        data-testid="widget-blast-search"
                      />
                    </div>
                    <button onClick={() => setUseFilter(true)} className="text-xs text-slate-500 hover:text-slate-700 underline whitespace-nowrap px-2">
                      Use filter
                    </button>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={selectAll} className="flex-1 py-1.5 text-sm bg-[#FFF0EB] text-[#0F1419] rounded-lg border border-[#D1D5DB] hover:bg-[#d4edec] transition-colors flex items-center justify-center gap-1.5 font-medium" data-testid="widget-select-all-btn">
                      <CheckSquare size={13} /> All ({contacts.length})
                    </button>
                    <button onClick={clearAll} className="flex-1 py-1.5 text-sm bg-slate-100 text-slate-600 rounded-lg border border-slate-200 hover:bg-slate-200 transition-colors flex items-center justify-center gap-1.5 font-medium" data-testid="widget-clear-btn">
                      <X size={13} /> Clear
                    </button>
                  </div>
                  {/* Contact list */}
                  {loadingContacts ? (
                    <div className="flex items-center justify-center py-6">
                      <div className="w-5 h-5 border-2 border-[#FF5C35] border-t-transparent rounded-full animate-spin mr-2" />
                      <span className="text-sm text-slate-500">Loading contacts...</span>
                    </div>
                  ) : contacts.length > 0 ? (
                    <div className="max-h-48 overflow-y-auto divide-y divide-slate-100 rounded-xl border border-slate-200 bg-white">
                      {contacts.slice(0, 100).map(c => (
                        <div
                          key={c.id}
                          onClick={() => toggleContact(c.id)}
                          className={`flex items-center gap-2.5 px-3 py-2 cursor-pointer hover:bg-slate-50 transition-colors ${selectedIds.includes(c.id) ? "bg-[#FFF0EB]" : ""}`}
                          data-testid={`widget-contact-${c.id}`}
                        >
                          {selectedIds.includes(c.id)
                            ? <CheckSquare size={14} className="text-[#FF5C35] shrink-0" />
                            : <Square size={14} className="text-slate-300 shrink-0" />}
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-slate-800 truncate">{c.first_name} {c.last_name}</p>
                            <p className="text-xs text-slate-400 truncate">{c.phone} {c.stage ? `· ${c.stage}` : ""}</p>
                          </div>
                        </div>
                      ))}
                      {contacts.length > 100 && (
                        <p className="text-center text-sm text-slate-400 py-2">Showing first 100 of {contacts.length}</p>
                      )}
                    </div>
                  ) : null}
                  {selectedIds.length > 0 && (
                    <p className="text-sm text-[#FF5C35] font-semibold mt-3 text-center" data-testid="widget-selected-count">
                      {selectedIds.length} contact{selectedIds.length !== 1 ? "s" : ""} selected
                    </p>
                  )}
                </>
              )}
            </div>
          )}
        </div>

        {/* Message Composer Section */}
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <p className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
            <Send size={15} /> Compose Message
          </p>
          <div className="flex flex-wrap gap-1.5 mb-3">
            {MERGE_FIELDS.map(f => (
              <button
                key={f}
                onClick={() => setBody(b => b + f)}
                className="text-xs px-2.5 py-1 bg-[#FFF0EB] text-[#0F1419] border border-[#D1D5DB] rounded-full hover:bg-[#d4edec] transition-colors font-medium"
                data-testid={`widget-merge-${f}`}
              >
                {f}
              </button>
            ))}
          </div>
          <textarea
            value={body}
            onChange={e => setBody(e.target.value)}
            rows={4}
            placeholder="Hi {first_name}, this is your agent..."
            className="w-full text-sm px-3 py-2.5 border border-slate-300 rounded-xl resize-none focus:outline-none focus:ring-2 focus:ring-[#FF5C35]"
            data-testid="widget-blast-body"
          />
          {/* SMS Guardrails */}
          <div className="mt-1.5 space-y-1">
            <div className="flex items-center justify-between">
              <span className={`text-xs font-medium ${body.length > SMS_MAX ? "text-red-600" : body.length > 250 ? "text-amber-600" : "text-slate-400"}`}>
                {body.length}/{SMS_MAX} chars · {body.length <= 160 ? 1 : Math.ceil(body.length / 153)} segment{body.length > 160 ? "s" : ""}
              </span>
              <span className="text-xs text-slate-400">"Reply STOP" appended</span>
            </div>
            <div className="h-1 bg-slate-200 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all ${body.length > SMS_MAX ? "bg-red-500" : body.length > 250 ? "bg-amber-500" : "bg-[#FF5C35]"}`}
                style={{ width: `${Math.min((body.length / SMS_MAX) * 100, 100)}%` }}
              />
            </div>
            {body.length > SMS_MAX && (
              <p className="text-xs text-red-600">Message too long — carriers will block delivery. Remove signatures and keep it short.</p>
            )}
            {URL_PATTERN.test(body) && (
              <p className="text-xs text-red-600">URLs not allowed in blasts — carriers flag them as spam.</p>
            )}
          </div>
        </div>

        {/* Message Preview */}
        {body.trim() && (
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Preview</p>
            <div className="bg-slate-900 rounded-xl p-3.5 text-sm text-white leading-relaxed">
              {body
                .replace(/\{first_name\}/g, "Sarah")
                .replace(/\{last_name\}/g, "Johnson")
                .replace(/\{agent_name\}/g, agentName)
                .replace(/\{address\}/g, "[contact's property]")}
              <span className="block mt-2 text-slate-400 text-xs">Reply STOP to unsubscribe</span>
            </div>
          </div>
        )}

        {/* Send Button */}
        <button
          onClick={sendBlast}
          disabled={sending || !body.trim() || body.length > SMS_MAX || URL_PATTERN.test(body) || (audienceMode === "synced" && useFilter ? getFilterCount() === 0 : selectedIds.length === 0)}
          className="w-full py-3 text-sm font-semibold bg-[#FF5C35] text-white rounded-xl hover:bg-[#E84E29] disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
          data-testid="widget-blast-send-btn"
        >
          {sending ? (
            <span className="flex items-center gap-2">
              <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              {blastProgress ? `Sending... ${blastProgress.processed}/${blastProgress.sendable}` : "Sending..."}
            </span>
          ) : (audienceMode === "synced" && useFilter) ? (
            <span className="flex items-center gap-2"><Zap size={16} /> Blast to {getFilterCount().toLocaleString()} {filterTag && filterStage ? `${filterStage} + ${filterTag}` : filterTag || filterStage || "all"} contacts</span>
          ) : (
            <span className="flex items-center gap-2"><Zap size={16} /> Blast to {selectedIds.length} contacts</span>
          )}
        </button>
        {blastProgress && (
          <div className="mt-2">
            <div className="h-1.5 bg-slate-200 rounded-full overflow-hidden">
              <div className="h-full bg-[#FF5C35] rounded-full transition-all duration-500" style={{ width: `${blastProgress.sendable > 0 ? (blastProgress.processed / blastProgress.sendable * 100) : 0}%` }} />
            </div>
            <p className="text-xs text-slate-400 text-center mt-1">
              {blastProgress.sent} sent · {blastProgress.processed} of {blastProgress.sendable} processed
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
